import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { authAPI } from '../api';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const BG_IMAGES = [
  '/front1.png', '/front2.png', '/front3.png', '/front4.png',
  '/front5.png', '/front6.png', '/front7.png',
];

const FEATURES = [
  {
    num: '01',
    title: '24/7 Fleet Access',
    body: 'Our 22-truck fleet is available around the clock. No waiting, no scheduling — grab a truck and hit the road.',
    testId: 'feature-reliability',
  },
  {
    num: '02',
    title: 'Free Maintenance',
    body: 'Cheaper or completely covered repairs on all company vehicles. Keep driving without the bills.',
    testId: 'feature-support',
  },
  {
    num: '03',
    title: 'Active Community',
    body: 'Training programs, company runs, and a crew of drivers that actually show up for each other.',
    testId: 'feature-team',
  },
];

const Home = () => {
  const [authUrl, setAuthUrl] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('gty_token');
    setIsLoggedIn(!!token);
    authAPI.getDiscordAuthUrl().then((r) => setAuthUrl(r.data.url)).catch(console.error);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setBgIndex(i => (i + 1) % BG_IMAGES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const handleLogin = () => { if (authUrl) window.location.href = authUrl; };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="hero-section" data-testid="hero-section">
        <AnimatePresence>
          <motion.img
            key={bgIndex}
            src={BG_IMAGES[bgIndex]}
            alt="GTY Trucking"
            className="hero-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          />
        </AnimatePresence>
        <div className="hero-overlay" />
        <div className="hero-content w-full">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-4xl"
            >
              <div className="mb-6">
                <span className="overline">Professional Trucking Services</span>
              </div>
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-black mb-8 tracking-tighter leading-none uppercase">
                GO TRUCK<br />YOURSELF
              </h1>
              <div className="border-l-4 border-purple-600 pl-6 mb-12 max-w-2xl">
                <p className="text-xl md:text-2xl text-[#a1a1aa] leading-relaxed">
                  Premier trucking company located next to YouTools. Join our community for 24/7 fleet access, free maintenance, professional training, and company events.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                {isLoggedIn ? (
                  <a
                    href="/dashboard"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="go-to-dashboard-button"
                  >
                    Dashboard
                    <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                  </a>
                ) : (
                  <button
                    onClick={handleLogin}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="discord-login-button"
                  >
                    Login with Discord
                    <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                )}
                <Link to="/contact" className="btn-secondary" data-testid="learn-more-button">
                  Contact Us
                </Link>
              </div>

              {/* Hiring strip — static, no pulsing dot */}
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.85, duration: 0.45 }}
                className="mt-8 inline-flex"
              >
                <Link
                  to="/contact"
                  className="group flex items-center gap-3 border-l-4 border-[#7c3aed] bg-[#7c3aed]/5 hover:bg-[#7c3aed]/10 pl-4 pr-5 py-2.5 transition-colors duration-150"
                >
                  <span className="text-[#7c3aed] text-sm font-black leading-none select-none">▸</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.22em] text-[#a78bfa]">
                    We're hiring — Apply now
                  </span>
                  <ArrowRight className="w-3 h-3 text-[#a78bfa] group-hover:translate-x-1 transition-transform duration-150" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────── */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="overline">What We Offer</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mt-4 tracking-tighter uppercase">
              Why Join GTY?
            </h2>
          </div>

          {/* Numbered grid — no icon boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-[#27272a] divide-y-2 md:divide-y-0 md:divide-x-2 divide-[#27272a]">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.1 }}
                className="p-8 md:p-10 flex flex-col gap-6"
                data-testid={f.testId}
              >
                <span className="mono-number text-[#7c3aed] text-xs font-black tracking-[0.3em]">
                  {f.num}
                </span>
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-3">{f.title}</h3>
                  <p className="text-[#71717a] leading-relaxed text-sm">{f.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
